import { z } from 'zod'

import { type AppSupabaseClient } from '@/platform/supabase/client'
import { callRpc } from '@/platform/supabase/rpc'

/**
 * Learner profile API (FR-02) and child-mode context (FR-03).
 *
 * Contracts: backend-contracts v0.1 §4.1–§4.2, implemented by
 * `supabase/migrations/20261031000002_learners_and_enrollment.sql`. Levels are
 * per domain (DB-02); this module is domain-neutral and passes level codes
 * through without interpreting them.
 */

export const enrollmentSchema = z.object({
  domain_code: z.string(),
  current_level: z.string(),
  target_level: z.string(),
})
export type Enrollment = z.infer<typeof enrollmentSchema>

export const learnerSchema = z.object({
  id: z.uuid(),
  nickname: z.string(),
  birth_year: z.number().int(),
  exam_date: z.string().nullable(),
  daily_minutes: z.number().int(),
  study_days: z.array(z.number().int()),
  interests: z.array(z.string()),
  enrollments: z.array(enrollmentSchema),
})
export type Learner = z.infer<typeof learnerSchema>

export const childModeContextSchema = z.object({
  learner_id: z.uuid(),
  nickname: z.string(),
})
export type ChildModeContext = z.infer<typeof childModeContextSchema>

/** Profile fields accepted by `create_learner` / `update_learner_profile`. */
export interface LearnerProfileInput {
  nickname: string
  birth_year: number
  exam_date: string | null
  daily_minutes: number
  study_days: number[]
  interests: string[]
}

export interface LevelSelection {
  domainCode: string
  currentLevel: string
  targetLevel: string
}

export interface LearnersApi {
  listLearners(): Promise<Learner[]>
  createLearner(profile: LearnerProfileInput, levels: LevelSelection): Promise<Learner>
  updateLearner(
    learnerId: string,
    profile: Partial<LearnerProfileInput>,
    levels?: LevelSelection,
  ): Promise<Learner>
  getChildModeContext(learnerId: string): Promise<ChildModeContext>
}

export function createLearnersApi(client: AppSupabaseClient): LearnersApi {
  return {
    listLearners: () => callRpc(client, 'list_learners', {}, z.array(learnerSchema)),

    createLearner: (profile, levels) =>
      callRpc(
        client,
        'create_learner',
        {
          p_profile: profile,
          p_domain_code: levels.domainCode,
          p_current_level: levels.currentLevel,
          p_target_level: levels.targetLevel,
        },
        learnerSchema,
      ),

    updateLearner: async (learnerId, profile, levels) => {
      let learner = await callRpc(
        client,
        'update_learner_profile',
        { p_learner_id: learnerId, p_patch: profile },
        learnerSchema,
      )
      if (levels !== undefined) {
        learner = await callRpc(
          client,
          'set_learner_domain_enrollment',
          {
            p_learner_id: learnerId,
            p_domain_code: levels.domainCode,
            p_current_level: levels.currentLevel,
            p_target_level: levels.targetLevel,
          },
          learnerSchema,
        )
      }
      return learner
    },

    getChildModeContext: (learnerId) =>
      callRpc(
        client,
        'get_child_mode_context',
        { p_learner_id: learnerId },
        childModeContextSchema,
      ),
  }
}
